import {
  ControlOutlined,
  DeleteOutlined,
  DownOutlined,
  FileOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import {
  collectAuthoredDocumentNames,
  foldDocumentName,
  uniqueDefaultDocumentLabel,
} from '@bmo/form-core';
import { Button, Checkbox, Drawer, Input, InputNumber, Switch, Tooltip, Typography } from 'antd';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AdditionalDocumentEntry,
  DEFAULT_MAX_OPTIONAL_DOCUMENTS,
  type FileEntryCondition,
  MAX_COMBINED_MAX_SIZE_MB,
  MAX_DOCUMENT_DESCRIPTION_LENGTH,
  MAX_DOCUMENT_MAX_FILE_SIZE_MB,
  MAX_OPTIONAL_DOCUMENT_COUNT,
  MIN_COMBINED_MAX_SIZE_MB,
  MIN_DOCUMENT_MAX_FILE_SIZE_MB,
  MIN_OPTIONAL_DOCUMENT_COUNT,
  type WorkflowStep,
  workflowsApi,
} from '@/api/workflows';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';

import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';
import { EntryLabelEditor } from './entries/EntryLabelEditor';

const { Text } = Typography;

const I18N_NS = 'workflow.steps.additionalDocuments';
// BMO blue — matches the condition indicator used on the step rail
// (AntD primary) and on the PDF Entries drawer's per-entry button so the
// "has conditions" cue is one colour across the editor.
const TINT_FG = '#0179C1';

// Slightly smaller than the global 32x32 icon button — these sit in a tight
// header row inside each entry card. Mirrors PdfEntriesDrawer's `iconBtn`.
const iconBtn = {
  ...ICON_BTN_PROPS,
  style: { ...ICON_BTN_PROPS.style, width: 26, height: 26, fontSize: 12, borderRadius: 6 },
} as const;

/**
 * Supported extensions an originator can permit per document entry. Keep in sync
 * with WorkflowStep.SupportedExtensions on the BE — the BE re-validates at runtime so
 * a tampered FE can't bypass this list. Grouping is purely UX; the value sent is the
 * raw extension string (lowercase, no dot).
 */
const EXTENSION_GROUPS: ReadonlyArray<{ groupKey: string; values: ReadonlyArray<string> }> = [
  { groupKey: 'documents', values: ['pdf', 'doc', 'docx', 'txt'] },
  { groupKey: 'spreadsheets', values: ['xls', 'xlsx', 'csv'] },
  { groupKey: 'images', values: ['jpg', 'jpeg', 'png', 'webp'] },
];

type Props = {
  templateId: string;
  step: WorkflowStep | null;
  /** All steps in the workflow — passed in so the per-document condition modal
   *  can enumerate selectable source fields across every DynamicForm step. */
  allSteps: WorkflowStep[];
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
};

/** True when the entry carries at least one gating condition. */
function hasCondition(entry: AdditionalDocumentEntry): boolean {
  return (entry.conditions?.length ?? 0) > 0;
}

/**
 * Free-form max-file-size input shared by the per-entry cards and the
 * step-level optional bucket. Replaces the old 10/15/20 MB `Segmented`
 * picker now that the BE accepts any value in
 * [{@link MIN_DOCUMENT_MAX_FILE_SIZE_MB}, {@link MAX_DOCUMENT_MAX_FILE_SIZE_MB}].
 *
 * Types into local `draft` state and only calls `onCommit` on blur/Enter —
 * committing per keystroke would fire a PATCH per digit and briefly send
 * out-of-range intermediate values (e.g. the "2" in "20"). `draft` resyncs
 * from `value` whenever it changes, which also covers the caller's
 * optimistic-update rollback (its `value` reverts on a failed PATCH).
 *
 * Deliberately skips InputNumber's `min`/`max` props: those auto-clamp the
 * typed value on blur with no feedback, which reads as the number silently
 * changing under the originator. Out-of-range input instead gets an inline
 * error and is never committed — no popup, no silent correction.
 */
function BoundedNumberInput({
  label,
  value,
  min,
  max,
  unit,
  errorText,
  placeholder,
  allowEmpty = false,
  onCommit,
  disabled,
}: {
  label: string;
  /** Null renders an empty box — used by the combined caps, where null means "no cap set". */
  value: number | null;
  min: number;
  max: number;
  /** `addonAfter` — "MB" / "Mo", or absent for a bare count. */
  unit?: string;
  /** Already-interpolated out-of-range message. */
  errorText: string;
  placeholder?: string;
  /** When true, clearing the box is a valid state that commits null (removes the cap). */
  allowEmpty?: boolean;
  onCommit: (value: number | null) => void;
  disabled: boolean;
}) {
  const [draft, setDraft] = useState<number | null>(value);
  useEffect(() => setDraft(value), [value]);

  const isEmpty = draft === null;
  const isOutOfRange = isEmpty ? !allowEmpty : draft < min || draft > max;

  const commit = () => {
    if (isOutOfRange) return;
    if (draft !== value) onCommit(draft);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text strong style={{ fontSize: 13, color: '#111827' }}>
          {label}
        </Text>
        <InputNumber
          size="small"
          precision={0}
          status={isOutOfRange ? 'error' : undefined}
          value={draft}
          disabled={disabled}
          onChange={(v) => setDraft(v)}
          onBlur={commit}
          onPressEnter={commit}
          addonAfter={unit}
          placeholder={placeholder}
          style={{ width: 150 }}
        />
      </div>
      {isOutOfRange && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {errorText}
        </Text>
      )}
    </div>
  );
}

/**
 * The per-file cap, kept as a thin wrapper over {@link BoundedNumberInput} so its two existing
 * call sites and its i18n keys are untouched by the combined caps arriving beside it.
 */
function MaxFileSizeInput({
  value,
  onCommit,
  disabled,
}: {
  value: number;
  onCommit: (mb: number) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  return (
    <BoundedNumberInput
      label={t(`${I18N_NS}.maxFileSize`)}
      value={value}
      min={MIN_DOCUMENT_MAX_FILE_SIZE_MB}
      max={MAX_DOCUMENT_MAX_FILE_SIZE_MB}
      // Routed through i18n rather than hard-coded, which is what put an English unit on the
      // French drawer.
      unit={t(`${I18N_NS}.unitMb`)}
      errorText={t(`${I18N_NS}.maxFileSizeError`, {
        min: MIN_DOCUMENT_MAX_FILE_SIZE_MB,
        max: MAX_DOCUMENT_MAX_FILE_SIZE_MB,
      })}
      onCommit={(mb) => mb !== null && onCommit(mb)}
      disabled={disabled}
    />
  );
}

/**
 * The optional per-entry description the advisor reads under this document's name — English and
 * French, each capped at {@link MAX_DOCUMENT_DESCRIPTION_LENGTH}.
 *
 * <h3>Commit on blur, not per keystroke</h3>
 * Same rule {@link BoundedNumberInput} follows, and for a sharper reason here: a PATCH per
 * keystroke on a 500-character box is 500 whole-state saves of the entry, each racing the one
 * before it. The draft resyncs from `value` so the caller's refetch — and its rollback on a failed
 * save — still lands in the box.
 *
 * <h3>Why two boxes rather than a modal</h3>
 * The French label gets a modal because it shares a row with the name, the conditions button and
 * the delete; this sits in the expanded body where there is room, and an originator writing
 * guidance usually writes both languages in one sitting. The French box states its own fallback in
 * its placeholder rather than hiding the rule in a tooltip.
 *
 * <h3>A failed save KEEPS what was typed</h3>
 * Deliberately unlike the optimistic number pickers on this drawer, which snap back to the
 * persisted value when their PATCH fails. Those hold one digit the originator can retype in a
 * second; this holds up to 500 characters of prose, and throwing a paragraph away over a
 * transient 500 is the worse failure by a distance. The text stays, `handleApiError` says the
 * save failed, and the next blur retries it — the draft still differs from what is persisted, so
 * the commit fires again on its own.
 *
 * <p>
 * `maxLength` + the counter keep the cap visible while typing, and they are what stops an
 * over-length value ever being authored here. The inline error is for the one case they cannot
 * cover: a STORED description longer than the cap. `Rehydrate` deliberately does not refuse one
 * (an unloadable template is worse than a mis-capped field), so a row written outside this editor
 * can arrive over-length — and it must be named and blocked from being saved back rather than
 * silently rejected by the server on blur.
 * </p>
 */
function DescriptionEditor({
  label,
  value,
  placeholder,
  onCommit,
  disabled,
}: {
  label: string;
  /** Null when nothing is authored — rendered as an empty box. */
  value: string | null | undefined;
  placeholder: string;
  /** Fired on blur with the trimmed text, or null when the box was emptied (which CLEARS it). */
  onCommit: (next: string | null) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => setDraft(value ?? ''), [value]);
  // Per MOUNTED instance, so the two boxes on a card — and the pair on every other expanded card —
  // each point their label at their own textarea rather than all at the first one.
  const id = useId();

  const tooLong = draft.length > MAX_DOCUMENT_DESCRIPTION_LENGTH;

  const commit = () => {
    if (tooLong) return;
    const next = draft.trim() === '' ? null : draft.trim();
    if (next !== (value ?? null)) onCommit(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* A real <label>, not the Text span it looks like: the span above a box reads as its label
          to the eye while naming nothing at all to a screen reader, which announces an unlabelled
          edit field with only the placeholder as a clue — and a placeholder disappears the moment
          anything is typed. Same correction AdditionalDocumentsStep made for the advisor's box. */}
      <label htmlFor={id} style={{ fontSize: 12, color: '#475569' }}>
        {label}
      </label>
      <Input.TextArea
        id={id}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        placeholder={placeholder}
        maxLength={MAX_DOCUMENT_DESCRIPTION_LENGTH}
        showCount
        status={tooLong ? 'error' : undefined}
        aria-invalid={tooLong}
        disabled={disabled}
        autoSize={{ minRows: 2, maxRows: 6 }}
      />
      {tooLong && (
        // Clears AntD's `showCount` counter, which is absolutely positioned a full line BELOW the
        // box and OUT OF FLOW — at an ordinary gap this message is drawn straight through it.
        // 22px is AntD's own line height at the themed 14px base; the 4px gap comes from the flex
        // parent. The same pair of numbers is spelled out in AdditionalDocumentsStep.
        <Text type="danger" style={{ fontSize: 12, marginTop: 22 }}>
          {t(`${I18N_NS}.descriptionTooLong`, { max: MAX_DOCUMENT_DESCRIPTION_LENGTH })}
        </Text>
      )}
    </div>
  );
}

/**
 * Drawer for configuring an AdditionalDocuments step. Replicates the
 * PdfFileUpload drawer's entry-list pattern (PdfEntriesDrawer): the
 * originator adds named document slots, each with its own accepted file
 * types, a required flag, and an opt-out of the platform's default
 * non-PDF -> PDF conversion. Unlike PdfFileUpload, the originator never
 * uploads a file here — the advisor supplies it at runtime.
 */
export function AdditionalDocumentEntriesDrawer({
  templateId,
  step,
  allSteps,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const [conditionTarget, setConditionTarget] = useState<AdditionalDocumentEntry | null>(null);
  // New entries default to expanded so the originator lands on the file-type
  // picker right after clicking Add Document.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  // Optimistic mirror for the optional-bucket size picker — paints the new
  // pill instantly instead of waiting for PUT → refetch → re-render. Resets
  // whenever the step prop's persisted value changes.
  const [optimisticBucketMaxSize, setOptimisticBucketMaxSize] = useState<number | null>(null);
  const stepBucketMaxSize = step?.optionalDocumentMaxFileSizeMb;
  useEffect(() => setOptimisticBucketMaxSize(null), [stepBucketMaxSize]);
  // Same optimistic-mirror pattern as the size picker above — paints the
  // Switch instantly instead of waiting for PUT → refetch → re-render.
  const [optimisticShowSection, setOptimisticShowSection] = useState<boolean | null>(null);
  const stepShowSection = step?.showOptionalDocumentsSection;
  useEffect(() => setOptimisticShowSection(null), [stepShowSection]);
  // Same pattern again for the three caps. `undefined` is "no optimistic value" here rather than
  // `null`, because null is a MEANINGFUL value for the two combined caps — it is how the
  // originator says "no cap" — so it cannot double as the sentinel.
  const [optimisticMandatoryCombined, setOptimisticMandatoryCombined] = useState<
    number | null | undefined
  >(undefined);
  const stepMandatoryCombined = step?.mandatoryCombinedMaxSizeMb;
  useEffect(() => setOptimisticMandatoryCombined(undefined), [stepMandatoryCombined]);
  const [optimisticOptionalCombined, setOptimisticOptionalCombined] = useState<
    number | null | undefined
  >(undefined);
  const stepOptionalCombined = step?.optionalCombinedMaxSizeMb;
  useEffect(() => setOptimisticOptionalCombined(undefined), [stepOptionalCombined]);
  const [optimisticMaxOptionalDocs, setOptimisticMaxOptionalDocs] = useState<number | null>(null);
  const stepMaxOptionalDocs = step?.maxOptionalDocuments;
  useEffect(() => setOptimisticMaxOptionalDocs(null), [stepMaxOptionalDocs]);

  if (!step) return null;

  const stepTitle = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
  const entries = step.additionalDocumentEntries ?? [];
  // Optimistic value wins so the Switch and the warning beneath it never disagree mid-PUT.
  const showSection = optimisticShowSection ?? step.showOptionalDocumentsSection;

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAddDocument = async () => {
    setCreating(true);
    try {
      // Counted up ("Document Name 2") when the plain default is already taken: document names must
      // be unique, so a second click would otherwise be refused — a rejection on the most common
      // authoring action there is, for a name the originator never chose.
      const created = await workflowsApi.addAdditionalDocumentEntry(templateId, step.id, {
        label: uniqueDefaultDocumentLabel(
          t(`${I18N_NS}.defaultLabel`),
          collectAuthoredDocumentNames(allSteps),
        ),
      });
      setExpandedIds((prev) => new Set(prev).add(created.id));
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.addFailed`));
    } finally {
      setCreating(false);
    }
  };

  // The ONLY handler that sends `extensions`. Every other control on this drawer omits it, so a
  // save of a size cap or the document count cannot rewrite the bucket's accepted types — which
  // it did while each handler echoed the list back from a `step` prop captured at render time.
  const handleOptionalBucketExtensionsChange = async (extensions: string[]) => {
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, { extensions });
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleOptionalBucketMaxFileSizeChange = async (mb: number) => {
    setOptimisticBucketMaxSize(mb);
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, {
        maxFileSizeMb: mb,
      });
      await onChanged();
    } catch (err) {
      setOptimisticBucketMaxSize(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleShowSectionChange = async (checked: boolean) => {
    setOptimisticShowSection(checked);
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, {
        showSection: checked,
      });
      await onChanged();
    } catch (err) {
      setOptimisticShowSection(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleMandatoryCombinedChange = async (mb: number | null) => {
    setOptimisticMandatoryCombined(mb);
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, {
        mandatoryCombinedMaxSizeMb: mb ?? undefined,
      });
      await onChanged();
    } catch (err) {
      setOptimisticMandatoryCombined(undefined);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleOptionalCombinedChange = async (mb: number | null) => {
    setOptimisticOptionalCombined(mb);
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, {
        optionalCombinedMaxSizeMb: mb ?? undefined,
      });
      await onChanged();
    } catch (err) {
      setOptimisticOptionalCombined(undefined);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleMaxOptionalDocumentsChange = async (count: number | null) => {
    if (count === null) return;
    setOptimisticMaxOptionalDocs(count);
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, {
        maxOptionalDocuments: count,
      });
      await onChanged();
    } catch (err) {
      setOptimisticMaxOptionalDocs(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  return (
    <Drawer open={!!step} onClose={onClose} title={stepTitle} width={640} destroyOnHidden>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={creating}
          disabled={!editable}
          onClick={handleAddDocument}
          style={{ alignSelf: 'flex-start', borderRadius: 999, padding: '0 18px' }}
        >
          {t(`${I18N_NS}.addDocument`)}
        </Button>

        {entries.length === 0 && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t(`${I18N_NS}.empty`)}
          </Text>
        )}

        {entries.map((entry) => (
          <EntryCard
            key={entry.id}
            templateId={templateId}
            stepId={step.id}
            entry={entry}
            editable={editable}
            isExpanded={expandedIds.has(entry.id)}
            allSteps={allSteps}
            onToggleExpanded={() => toggleExpanded(entry.id)}
            onConfigureCondition={setConditionTarget}
            onChanged={onChanged}
          />
        ))}

        {/* The MANDATORY section's combined cap. Sits beneath the entry list it governs, in the
            same chrome as the optional card below, so the drawer reads as two sections with their
            own budgets — which is exactly what the advisor sees at runtime. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#fff',
            marginTop: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: '#f8fafc',
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <Text strong style={{ fontSize: 14, color: '#111827' }}>
              {t(`${I18N_NS}.mandatorySectionDivider`)}
            </Text>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {t(`${I18N_NS}.mandatoryCombinedDescription`)}
            </Text>
            {/* The originator-side answer to "which rows does this actually cover". Without it,
                setting a cap on a step whose entries are all optional looks like it did
                something — and nothing at runtime would ever be weighed against it. */}
            {entries.length > 0 && !entries.some((e) => e.isRequired) && (
              <Text type="warning" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                {t(`${I18N_NS}.mandatoryCombinedNoRequired`)}
              </Text>
            )}
          </div>
          <div style={{ padding: '14px 16px 16px' }}>
            <BoundedNumberInput
              label={t(`${I18N_NS}.combinedMaxSize`)}
              value={
                optimisticMandatoryCombined !== undefined
                  ? optimisticMandatoryCombined
                  : (step.mandatoryCombinedMaxSizeMb ?? null)
              }
              min={MIN_COMBINED_MAX_SIZE_MB}
              max={MAX_COMBINED_MAX_SIZE_MB}
              unit={t(`${I18N_NS}.unitMb`)}
              placeholder={t(`${I18N_NS}.combinedMaxSizePlaceholder`)}
              // Empty is a real answer: it means "no combined cap", which is how every step
              // authored before this setting behaves and what an originator clears it back to.
              allowEmpty
              errorText={t(`${I18N_NS}.combinedMaxSizeError`, {
                min: MIN_COMBINED_MAX_SIZE_MB,
                max: MAX_COMBINED_MAX_SIZE_MB,
              })}
              onCommit={(mb) => void handleMandatoryCombinedChange(mb)}
              disabled={!editable}
            />
          </div>
        </div>

        {/* Optional-bucket section — visually framed as its own card so it
            reads as a distinct concern from the named-entry list above.
            Mirrors the EntryCard's chrome (light border, rounded corners,
            soft-blue header strip) for visual consistency. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#fff',
            marginTop: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: '#f8fafc',
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <Text strong style={{ fontSize: 14, color: '#111827' }}>
                {t(`${I18N_NS}.optionalBucketDivider`)}
              </Text>
              <Tooltip title={t(`${I18N_NS}.showSectionTooltip`)}>
                <Switch
                  size="small"
                  checked={showSection}
                  onChange={(checked) => void handleShowSectionChange(checked)}
                  disabled={!editable}
                />
              </Tooltip>
            </div>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {t(`${I18N_NS}.optionalBucketDescription`)}
            </Text>
            {/* The toggle alone does NOT put this section in front of an advisor — the runtime also
                requires at least one accepted file type, because a dropzone that accepts nothing is
                not a section worth rendering (see AdditionalDocumentsStep's `bucketVisible`). That
                second condition is invisible from here, so an originator who flips the switch and
                picks no types walks away believing the section is live when it will never appear.
                Say so on the spot rather than letting them find out from a runtime that renders
                nothing and explains nothing. */}
            {showSection && (step.optionalDocumentExtensions ?? []).length === 0 && (
              <Text type="warning" style={{ fontSize: 12, marginTop: 6, display: 'block' }}>
                {t(`${I18N_NS}.showSectionNeedsTypes`)}
              </Text>
            )}
          </div>
          <Checkbox.Group
            value={step.optionalDocumentExtensions}
            onChange={(vals) => handleOptionalBucketExtensionsChange(vals as string[])}
            disabled={!editable}
            style={{ width: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
                padding: '14px 16px 16px',
              }}
            >
              {EXTENSION_GROUPS.map(({ groupKey, values }) => (
                <div key={groupKey}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                    {t(`${I18N_NS}.groups.${groupKey}`)}
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                    {values.map((ext) => (
                      <Checkbox key={ext} value={ext} style={{ alignItems: 'center' }}>
                        {`.${ext}`}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
              {/* Step-level size cap for the optional bucket. Same input the
                  per-entry cards use; persists via the same setOptional
                  endpoint that handles the extension list. */}
              <div
                style={{
                  paddingTop: 6,
                  borderTop: '1px solid #e5e7eb',
                  marginTop: 4,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <MaxFileSizeInput
                    value={optimisticBucketMaxSize ?? step.optionalDocumentMaxFileSizeMb}
                    onCommit={(mb) => void handleOptionalBucketMaxFileSizeChange(mb)}
                    disabled={!editable}
                  />
                  <BoundedNumberInput
                    label={t(`${I18N_NS}.combinedMaxSize`)}
                    value={
                      optimisticOptionalCombined !== undefined
                        ? optimisticOptionalCombined
                        : (step.optionalCombinedMaxSizeMb ?? null)
                    }
                    min={MIN_COMBINED_MAX_SIZE_MB}
                    max={MAX_COMBINED_MAX_SIZE_MB}
                    unit={t(`${I18N_NS}.unitMb`)}
                    placeholder={t(`${I18N_NS}.combinedMaxSizePlaceholder`)}
                    allowEmpty
                    errorText={t(`${I18N_NS}.combinedMaxSizeError`, {
                      min: MIN_COMBINED_MAX_SIZE_MB,
                      max: MAX_COMBINED_MAX_SIZE_MB,
                    })}
                    onCommit={(mb) => void handleOptionalCombinedChange(mb)}
                    disabled={!editable}
                  />
                  {/* No unit — this one counts documents, not megabytes. Unlike the two caps
                      above it is never null: its default is the number the advisor runtime
                      applied before the setting existed. */}
                  <BoundedNumberInput
                    label={t(`${I18N_NS}.maxOptionalDocuments`)}
                    value={
                      optimisticMaxOptionalDocs ??
                      step.maxOptionalDocuments ??
                      DEFAULT_MAX_OPTIONAL_DOCUMENTS
                    }
                    min={MIN_OPTIONAL_DOCUMENT_COUNT}
                    max={MAX_OPTIONAL_DOCUMENT_COUNT}
                    errorText={t(`${I18N_NS}.maxOptionalDocumentsError`, {
                      min: MIN_OPTIONAL_DOCUMENT_COUNT,
                      max: MAX_OPTIONAL_DOCUMENT_COUNT,
                    })}
                    onCommit={(count) => void handleMaxOptionalDocumentsChange(count)}
                    disabled={!editable}
                  />
                </div>
              </div>
            </div>
          </Checkbox.Group>
        </div>
      </div>

      <ConditionModal
        templateId={templateId}
        stepId={step.id}
        entry={conditionTarget}
        allSteps={allSteps}
        onClose={() => setConditionTarget(null)}
        onSaved={async () => {
          setConditionTarget(null);
          await onChanged();
        }}
      />
    </Drawer>
  );
}

/**
 * One document entry. Collapses into a single row (name + actions + a
 * summary of accepted file types); expands into a detail panel with the
 * file-type checkboxes and the "don't parse to PDF" override. No file
 * upload happens here — the advisor supplies the file at runtime.
 */
function EntryCard({
  templateId,
  stepId,
  entry,
  editable,
  isExpanded,
  allSteps,
  onToggleExpanded,
  onConfigureCondition,
  onChanged,
}: {
  templateId: string;
  stepId: string;
  entry: AdditionalDocumentEntry;
  editable: boolean;
  isExpanded: boolean;
  /** Every step, so a rename can be checked against every document name on the workflow. */
  allSteps: WorkflowStep[];
  onToggleExpanded: () => void;
  onConfigureCondition: (entry: AdditionalDocumentEntry) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  // Optimistic mirror of the max-size segment so the click registers
  // instantly instead of waiting for PATCH → refetch → re-render. Resets to
  // the persisted value whenever the entry prop changes (refetch landed).
  const [optimisticMaxSize, setOptimisticMaxSize] = useState<number | null>(null);
  useEffect(() => setOptimisticMaxSize(null), [entry.maxFileSizeMb]);

  const persist = async (
    patch: Partial<
      Pick<
        AdditionalDocumentEntry,
        | 'label'
        | 'labelFr'
        | 'allowedExtensions'
        | 'isRequired'
        | 'doNotParseToPdf'
        | 'maxFileSizeMb'
        | 'description'
        | 'descriptionFr'
      >
    >,
  ) => {
    // The edit is whole-state on the server: every property not sent back is cleared. So the
    // French label, the filename template and both descriptions ride along on every edit, not
    // just a rename.
    //
    // `!== undefined` rather than `??` for every NULLABLE member: null is a real value here — it
    // is how the originator clears a description — and `??` would read it as "not supplied" and
    // helpfully put the old text back, making the field impossible to empty.
    await workflowsApi.editAdditionalDocumentEntry(templateId, stepId, entry.id, {
      label: patch.label ?? entry.label,
      labelFr: patch.labelFr !== undefined ? patch.labelFr : (entry.labelFr ?? null),
      allowedExtensions: patch.allowedExtensions ?? entry.allowedExtensions,
      isRequired: patch.isRequired ?? entry.isRequired,
      doNotParseToPdf: patch.doNotParseToPdf ?? entry.doNotParseToPdf,
      maxFileSizeMb: patch.maxFileSizeMb ?? entry.maxFileSizeMb,
      filenameTemplate: entry.filenameTemplate ?? null,
      description:
        patch.description !== undefined ? patch.description : (entry.description ?? null),
      descriptionFr:
        patch.descriptionFr !== undefined ? patch.descriptionFr : (entry.descriptionFr ?? null),
    });
    await onChanged();
  };

  /**
   * Refuses a rename onto a name another document on this workflow already delivers under.
   *
   * Compares the §14.1 DOCNAME rather than the typed string, because that is what BMO actually
   * de-duplicates on — "Void Cheque" and "void-cheque" are one document to them. The server
   * enforces the same rule; this exists so the originator is told before the save round-trips.
   */
  const validateLabel = (nextEn: string): string | null => {
    const peer = collectAuthoredDocumentNames(allSteps, { excludeEntryId: entry.id }).get(
      foldDocumentName(nextEn),
    );
    return peer ? t('workflow.steps.entryLabel.duplicate', { peer }) : null;
  };

  const saveLabels = async (label: string, labelFr: string | null): Promise<boolean> => {
    try {
      await persist({ label, labelFr });
      return true;
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
      return false;
    }
  };

  const handleDelete = async () => {
    try {
      await workflowsApi.deleteAdditionalDocumentEntry(templateId, stepId, entry.id);
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.deleteFailed`));
    }
  };

  const handleToggleRequired = async (checked: boolean) => {
    try {
      await persist({ isRequired: checked });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleExtensionsChange = async (extensions: string[]) => {
    try {
      await persist({ allowedExtensions: extensions });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleDoNotParseChange = async (checked: boolean) => {
    try {
      await persist({ doNotParseToPdf: checked });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleDescriptionChange = async (
    which: 'description' | 'descriptionFr',
    next: string | null,
  ) => {
    try {
      await persist({ [which]: next });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleMaxFileSizeChange = async (mb: number) => {
    // Optimistic: paint the new pill on click, fire the PATCH in the
    // background. If it fails, drop the optimistic state so the picker
    // snaps back to whatever the BE has.
    setOptimisticMaxSize(mb);
    try {
      await persist({ maxFileSizeMb: mb });
    } catch (err) {
      setOptimisticMaxSize(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header row — name + actions. Always visible. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: '#f8fafc',
          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
        }}
      >
        <EntryLabelEditor
          label={entry.label}
          labelFr={entry.labelFr}
          editable={editable}
          renameLabel={t(`${I18N_NS}.rename`)}
          validate={validateLabel}
          onSave={saveLabels}
        >
          {hasCondition(entry) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t(`${I18N_NS}.conditionallyN`, { count: entry.conditions.length })}
            </Text>
          )}
        </EntryLabelEditor>

        <Tooltip
          title={t(hasCondition(entry) ? `${I18N_NS}.conditionEdit` : `${I18N_NS}.conditionAdd`)}
        >
          <Button
            {...iconBtn}
            aria-label={t(
              hasCondition(entry) ? `${I18N_NS}.conditionEdit` : `${I18N_NS}.conditionAdd`,
            )}
            icon={<ControlOutlined style={{ color: hasCondition(entry) ? TINT_FG : undefined }} />}
            disabled={!editable}
            onClick={() => onConfigureCondition(entry)}
          />
        </Tooltip>

        <ConfirmPopover
          title={t(`${I18N_NS}.deleteConfirmTitle`)}
          description={t(`${I18N_NS}.deleteConfirmDesc`)}
          onConfirm={handleDelete}
          disabled={!editable}
        >
          <Tooltip title={t(`${I18N_NS}.delete`)}>
            <Button
              {...iconBtn}
              aria-label={t(`${I18N_NS}.delete`)}
              danger
              icon={<DeleteOutlined />}
              disabled={!editable}
            />
          </Tooltip>
        </ConfirmPopover>

        <Tooltip title={t(`${I18N_NS}.requiredTooltip`)}>
          <Switch
            size="small"
            checked={entry.isRequired}
            onChange={handleToggleRequired}
            disabled={!editable}
          />
        </Tooltip>

        <Tooltip title={t(isExpanded ? `${I18N_NS}.collapse` : `${I18N_NS}.expand`)}>
          <Button
            {...iconBtn}
            aria-label={t(isExpanded ? `${I18N_NS}.collapse` : `${I18N_NS}.expand`)}
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={onToggleExpanded}
          />
        </Tooltip>
      </div>

      {/* Collapsed body — compact summary of accepted file types + parse override. */}
      {!isExpanded && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            flexWrap: 'wrap',
          }}
        >
          {entry.allowedExtensions.length > 0 ? (
            entry.allowedExtensions.map((ext) => (
              <Text
                key={ext}
                style={{
                  fontSize: 12,
                  padding: '2px 8px',
                  background: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: 999,
                }}
              >
                {`.${ext}`}
              </Text>
            ))
          ) : (
            <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
              {t(`${I18N_NS}.noTypesSelected`)}
            </Text>
          )}
          {entry.doNotParseToPdf && (
            <Text
              style={{
                fontSize: 12,
                padding: '2px 8px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: 999,
                color: '#92400E',
              }}
            >
              <FileOutlined style={{ marginRight: 4 }} />
              {t(`${I18N_NS}.notParsedBadge`)}
            </Text>
          )}
        </div>
      )}

      {/* Expanded body — file-type selector + the "don't parse to PDF" override.
          No file upload here; the advisor supplies the actual file at runtime. */}
      {isExpanded && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* FIRST in the expanded body, because it is about the document itself — what the
              advisor is being asked for — while everything below is about the file they may
              attach. It also mirrors the runtime, where this sits directly under the name and
              above the accepted formats. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <Text strong style={{ fontSize: 13, color: '#111827' }}>
                {t(`${I18N_NS}.description`)}
              </Text>
              {/* The link syntax, stated where it is typed. Markdown is not discoverable, and an
                  originator who pastes a bare URL gets text rather than a link — see RichText. */}
              <Text type="secondary" style={{ fontSize: 12, marginTop: 2, display: 'block' }}>
                {t(`${I18N_NS}.descriptionHint`)}
              </Text>
            </div>
            <DescriptionEditor
              label={t(`${I18N_NS}.descriptionEnLabel`)}
              value={entry.description}
              placeholder={t(`${I18N_NS}.descriptionPlaceholder`)}
              onCommit={(next) => void handleDescriptionChange('description', next)}
              disabled={!editable}
            />
            <DescriptionEditor
              label={t(`${I18N_NS}.descriptionFrLabel`)}
              value={entry.descriptionFr}
              placeholder={t(`${I18N_NS}.descriptionFrPlaceholder`)}
              onCommit={(next) => void handleDescriptionChange('descriptionFr', next)}
              disabled={!editable}
            />
          </div>

          <div style={{ borderTop: '1px solid #e5e7eb' }} />

          <Checkbox.Group
            value={entry.allowedExtensions}
            onChange={(vals) => handleExtensionsChange(vals as string[])}
            disabled={!editable}
            style={{ width: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {EXTENSION_GROUPS.map(({ groupKey, values }) => (
                <div key={groupKey}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                    {t(`${I18N_NS}.groups.${groupKey}`)}
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                    {values.map((ext) => (
                      <Checkbox key={ext} value={ext}>
                        {`.${ext}`}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Checkbox.Group>

          {/* Visual separator between the per-format allow-list (above) and the
              size + conversion-bypass row (below) so the two concerns read as
              distinct. */}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4 }} />

          {/* Per-entry combined file-size cap. */}
          <MaxFileSizeInput
            value={optimisticMaxSize ?? entry.maxFileSizeMb}
            onCommit={(mb) => void handleMaxFileSizeChange(mb)}
            disabled={!editable}
          />

          <Checkbox
            checked={entry.doNotParseToPdf}
            disabled={!editable}
            onChange={(e) => handleDoNotParseChange(e.target.checked)}
            // Center the box vertically with the label text — AntD's default
            // baseline alignment leaves the text sitting slightly below the
            // box on this font + size combination.
            style={{ alignItems: 'center' }}
          >
            {t(`${I18N_NS}.doNotParseToPdf`)}
          </Checkbox>
        </div>
      )}
    </div>
  );
}

/**
 * Thin wrapper around the shared ConditionsEditorModal — same model the
 * field/section/PDF visibility editors use. Owns persistence for the
 * additional-document case.
 */
function ConditionModal({
  templateId,
  stepId,
  entry,
  allSteps,
  onClose,
  onSaved,
}: {
  templateId: string;
  stepId: string;
  entry: AdditionalDocumentEntry | null;
  allSteps: WorkflowStep[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const isOpen = entry !== null;
  const handleSave = async (conditions: FileEntryCondition[], combinator: 'all' | 'any' | null) => {
    if (!entry) return;
    setSaving(true);
    try {
      await workflowsApi.setAdditionalDocumentEntryConditions(templateId, stepId, entry.id, {
        conditions,
        combinator,
      });
      await onSaved();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.conditionSaveFailed`));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ConditionsEditorModal
      open={isOpen}
      title={t(`${I18N_NS}.conditionModalTitle`)}
      intro={t(`${I18N_NS}.conditionIntro`)}
      initialConditions={entry?.conditions ?? []}
      initialCombinator={entry?.conditionCombinator ?? null}
      allSteps={allSteps}
      saving={saving}
      onCancel={onClose}
      onSave={handleSave}
    />
  );
}
